import Vue from 'vue';
import { getDomain } from '@/store/auth/auth.store';
import { AccessTokenKey, AUTH_STATE_COOKIE } from '@/store/auth/auth.store.constants';
// import { COMMUNITY_KEYS } from '@/auth/AuthRoot.vue';

const COMMUNITY_KEYS = {
  INTERCONTINENTAL_ALLIANCE: '6725a490-2547-11ee-a8ab-dfc8db3ddec2'
}

// Helper function to generate CSRF token
function generateRandomToken(length = 32) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

export const authHandler = {
  methods: {
    /**
     * Sets the authentication token and handles redirection
     * @param {string} loginResponse - The authentication response
     * @param {number} expiryDays - Number of days until token expires (default: 7)
     * Used in Login, LoginSSO and Register views
     */
    async handleAuthSuccess(loginResponse, expiryDays = 7) {
      console.log('handleAuthSuccess() START');
      console.log('loginResponse:', loginResponse);
      try {
        // Define isNexos at the beginning of the function
        const isNexos = process.env.VUE_APP_CUSTOMER === 'nexos';

        // Handle "OK" response differently
        if (typeof loginResponse === 'object' && loginResponse.message === 'OK') {
          console.log('loginResponse is an object and message is OK');
          
          // Store session information in Vuex store
          if (loginResponse.sessionInfo) {
            console.log('loginResponse session Info:', loginResponse.sessionInfo);
            // Check if there are active sessions
            if (loginResponse.sessionInfo.activeSessions >= 1) {
              // Show notification about active sessions
              const message = isNexos 
                ? `Tenías otra sesión activa que ha sido cerrada. Si el dispositivo era diferente al de esta sesión, recibirás un correo con los detalles.` 
                : `You had another active session that has been closed. If the device was different from this one, you will receive an email with the details.`;
              const title = isNexos ? 'Sesión activa' : 'Active session';
              
              this.$bvToast.toast(message, {
                title: title,
                variant: 'warning',
                solid: true,
                autoHideDelay: 2000
              });

              // Wait for 2 seconds before redirecting
              await new Promise(resolve => setTimeout(resolve, 2000));
            }

            // For Nexos, check if this is a new session while another is active
            if (isNexos) {
              const currentSession = this.$store.getters['session/currentSession'];
              if (currentSession && currentSession !== loginResponse.sessionInfo.id) {
                console.log("currentSession:", currentSession);
                // This is a new session while another is active
                // We should show a warning and handle the session conflict
                this.$bvModal.msgBoxConfirm(
                  'Another session is already active. This will log out the other session. Do you want to continue?',
                  {
                    title: 'Session Conflict',
                    okVariant: 'danger',
                    okTitle: 'Yes, continue',
                    cancelTitle: 'No, stay logged out'
                  }
                ).then(value => {
                  console.log("value from msgBoxConfirm:", value);
                  if (value) {
                    // User confirmed, proceed with new session
                    this.$store.dispatch('session/updateSessionInfo', loginResponse.sessionInfo);
                    this.handleRedirect();
                  } else {
                    // User cancelled, clear the auth state and redirect to login
                    console.log("removing auth state cookie...");
                    Vue.$cookies.remove(AUTH_STATE_COOKIE);
                    window.location.href = process.env.VUE_APP_MY_NECTIOS_URL;
                  }
                });
                return;
              }
            }
            
            // Normal flow - update session info
            this.$store.dispatch('session/updateSessionInfo', loginResponse.sessionInfo);
          }

          if (loginResponse.useBearer) {
            console.log('loginResponse.useBearer is true');
          } else {
            console.log("saving cookie with logged in state...");
            // Save cookie with logged in state
            Vue.$cookies.set(AUTH_STATE_COOKIE, 'authenticated', {
              httpOnly: false
            });
          
            // For "OK" response, we only handle redirection
            await this.handleRedirect();
            return;
          }
        }

        // Checking token in response...

        console.log('loginResponse.token:', loginResponse.token);
        // Validate token format for JWT
        if (!loginResponse.token) {
          throw new Error('Invalid token: token is required');
        }

        // Handle both string and JSON token formats
        const tokenString = typeof loginResponse === 'string' ? loginResponse : loginResponse.token;
        if (!tokenString || tokenString.split('.').length !== 3) {
          throw new Error('Invalid JWT format');
        }
        console.log('tokenString:', tokenString);

        // Set cookie expiry
        const date = new Date();
        date.setDate(date.getDate() + expiryDays);

        const isProduction = process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging';

        // Get parent domain for cross-subdomain sharing
        const parentDomain = getDomain();
        const domain = parentDomain ? `.${parentDomain}` : (process.env.VUE_APP_DOMAIN_1 || 'localhost');
        
        // Remove any 'app.' prefix from the domain if it exists
        const cleanDomain = domain.startsWith('.app.') ? domain.replace('.app.', '.') : domain;

        // Base cookie options
        const baseOptions = {
            path: '/',
            domain: cleanDomain,
            expires: date,
            secure: true,
            sameSite: 'Lax',  // Must be Lax for cross-origin requests
            priority: 'High'
        };

        // console.log('Setting cookies with cross-origin compatible options:', JSON.stringify(baseOptions));

        // Set cookies with explicit options for each one
        const setCookie = (name, value, additionalOptions = {}) => {
            const options = {
                ...baseOptions,
                ...additionalOptions
            };
            
            // Force critical security options while maintaining cross-origin compatibility
            options.secure = true;  // Always use HTTPS
            options.sameSite = 'Lax';  // Required for cross-origin
            options.priority = 'High';
            
            Vue.$cookies.set(name, value, options);
            
            // Verify cookie was set
            const cookie = Vue.$cookies.get(name);
            if (!cookie) {
                console.error(`Failed to set cookie: ${name}`);
            }
        };

        console.log('setting cookie with access token...');
        // Store the complete token with specific options
        setCookie(AccessTokenKey, loginResponse.token, {
            httpOnly: false // We need JS access for API calls
        });

        // Set auth state cookie
        setCookie(AUTH_STATE_COOKIE, 'authenticated', {
            httpOnly: false
        });

        // Set CSRF token - this is especially important for cross-origin requests
        const csrfToken = generateRandomToken();
        setCookie('XSRF-TOKEN', csrfToken, {
            httpOnly: false
        });

        // Log successful cookie setup
        // console.log('All cookies set successfully with cross-origin settings');

        // Handle redirection based on community type
        await this.handleRedirect();
      } catch (error) {
        console.error('Error in handleAuthSuccess:', error);
        throw error;
      }
    },

    /**
     * Checks if the current context is related to Intercontinental Alliance
     * @returns {boolean} True if the context is Intercontinental Alliance
     */
    isIntercontinentalAlliance() {
      // Check if we have a community object with the correct key
      if (this.community?.key === COMMUNITY_KEYS.INTERCONTINENTAL_ALLIANCE) {
        return true;
      }
      // Check the current URL
      return window.location.hostname.includes('intercontinentalalliance.com');
    },

    /**
     * Handles redirection after successful authentication
     */
    async handleRedirect() {
      const isNexos = process.env.VUE_APP_CUSTOMER === 'nexos';
      console.log('handleRedirect() START');
      if (!isNexos && this.isIntercontinentalAlliance()) {
        window.location.href = "https://app.intercontinentalalliance.com/intercontinental-alliance";
        return;
      }

      const vueAppUrl = process.env.VUE_APP_APP_URL_1 || 'http://localhost:8080';
      console.log('vueAppUrl:', vueAppUrl);

      let destinationPath = '';
      
      if (this.communitySlug) {
        const validSlug = typeof this.communitySlug === 'object' ? 
          (this.communitySlug.toString() || '') : 
          this.communitySlug;
        destinationPath = `/${validSlug}`;
      }

      const destinationURL = this.getValidPath(vueAppUrl + destinationPath);
      console.log('destinationURL:', destinationURL);

      // Check if destinationURL is a valid URL
      try {
        const url = new URL(destinationURL);
        console.log('validated url:', url);
      } catch (error) {
        console.error('Invalid destination URL:', destinationURL);
        return;
      }

      // Redirect to the destination URL
      window.location.href = destinationURL;
    },

    /**
     * Ensures path is valid and safe
     */
    getValidPath(path) {
      if (typeof path === 'object') {
        console.error('Invalid path object:', path);
        return '/';
      }
      return path;
    }
  }
}; 